if (!customElements.get('rs7gt-launch-film')) {
  customElements.define('rs7gt-launch-film', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.launchRoot = this.closest('rs7gt-launch');
      this.media = this.querySelector('[data-film-media]');
      this.video = this.querySelector('[data-film-video]');
      this.controls = this.querySelector('[data-film-controls]');
      this.playButton = this.querySelector('[data-film-play]');
      this.soundButton = this.querySelector('[data-film-sound]');
      this.playLabel = this.querySelector('[data-film-play-label]');
      this.soundLabel = this.querySelector('[data-film-sound-label]');
      this.status = this.querySelector('[data-film-status]');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.wasActive = false;
      this.motionEnabled = null;
      this.failedSources = new Set();
      this.userMuted ??= false;
      this.userPaused ??= false;
      this.manualPlayback = false;
      this.autoplayMuted = false;
      this.autoplayBlocked = false;
      const options = { signal: this.controller.signal };
      try {
        this.mediaConfig = JSON.parse(this.querySelector('[data-film-config]')?.textContent || '{}');
      } catch (_) {
        this.mediaConfig = {};
      }
      this.labels = this.mediaConfig.labels || {};
      this.playButton?.addEventListener('click', () => this.togglePlayback(), options);
      this.soundButton?.addEventListener('click', () => this.toggleSound(), options);
      this.motionQuery.addEventListener('change', () => this.updateMotion(), options);
      this.launchRoot?.addEventListener('rs7gt:motionchange', () => {
        cancelAnimationFrame(this.motionFrame);
        this.motionFrame = requestAnimationFrame(() => {
          this.motionFrame = 0;
          if (this.isConnected) this.updateMotion();
        });
      }, options);
      this.launchRoot?.addEventListener('rs7gt:pagecoveragechange', () => this.updateVideo(), options);
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.refresh(), 160);
      }, options);
      window.addEventListener('pageshow', () => this.refresh(), options);
      window.addEventListener('load', () => this.refresh(), options);
      document.addEventListener('visibilitychange', () => this.updateVideo(), options);
      this.querySelectorAll('img').forEach(image => {
        if (!image.complete) image.addEventListener('load', () => this.refresh(), options);
      });
      document.fonts?.ready.then(() => {
        if (!options.signal.aborted) this.refresh();
      });
      if (this.video) {
        this.video.muted = this.userMuted;
        this.video.loop = true;
        this.video.playsInline = true;
        this.video.addEventListener('playing', () => {
          if (this.canPlayVideo() && this.video.getAttribute('src') === this.videoSource()) {
            this.playing = true;
            this.video.hidden = false;
            this.syncControls();
          } else this.stopVideo();
        }, options);
        this.video.addEventListener('pause', () => {
          this.playing = false;
          this.syncControls();
        }, options);
        this.video.addEventListener('volumechange', () => this.syncControls(), options);
        this.video.addEventListener('error', () => {
          if (this.video.error && this.video.error.code !== 1) {
            this.failSource(this.video.getAttribute('src'));
          }
        }, options);
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (options.signal.aborted) return;
          const visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.6;
          this.visible = visible;
          this.updateVideo();
        }, { threshold: [0, 0.6, 1] });
        this.observer.observe(this);
      }
      this.updateMotion();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearTimeout(this.resizeTimer);
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = 0;
      this.destroyMotion();
      this.motionEnabled = null;
      this.visible = false;
      this.wasActive = false;
      this.manualPlayback = false;
      this.stopVideo();
      if (this.video?.hasAttribute('src')) {
        this.video.removeAttribute('src');
        this.video.load();
      }
      if (this.controls) this.controls.hidden = true;
    }

    updateMotion() {
      const editor = this.dataset.designMode === 'true' || this.launchRoot?.dataset.designMode === 'true';
      const enabled = !!(!this.motionQuery.matches && !editor && window.gsap && window.ScrollTrigger);
      if (enabled !== this.motionEnabled) {
        this.destroyMotion();
        this.motionEnabled = enabled;
        if (enabled && this.media) {
          try {
            window.gsap.registerPlugin(window.ScrollTrigger);
            this.animation = window.gsap.fromTo(this.media, { scale: 1.035 }, {
              scale: 1, ease: 'none', scrollTrigger: {
                trigger: this,
                start: () => this.flowTop() - window.innerHeight,
                end: () => this.flowTop(),
                scrub: 0.6, invalidateOnRefresh: true
              }
            });
          } catch (_) {
            this.destroyMotion();
            this.motionEnabled = false;
          }
        }
      }
      this.refresh();
    }

    flowTop() {
      return this.launchRoot?.flowTop?.(this) ?? this.getBoundingClientRect().top + window.scrollY;
    }

    videoSource() {
      const mp4Sources = variant => (Array.isArray(variant?.sources) ? variant.sources : [])
        .filter(source => source.url && /mp4/i.test(source.format || source.mime_type || ''))
        .sort((a, b) => (Number(a.width) || 0) - (Number(b.width) || 0));
      const mobile = window.innerWidth < 750;
      const mobileSources = mobile ? mp4Sources(this.mediaConfig?.mobile) : [];
      const sources = mobileSources.length ? mobileSources : mp4Sources(this.mediaConfig?.desktop);
      const target = Math.min(window.innerWidth * Math.min(window.devicePixelRatio || 1, 1.5), mobile ? 1280 : 1920);
      return sources.find(source => Number(source.width) >= target)?.url || sources[sources.length - 1]?.url;
    }

    isActive() {
      if (!this.observer) {
        const bounds = this.getBoundingClientRect();
        this.visible = bounds.top < window.innerHeight * 0.4 && bounds.bottom > window.innerHeight * 0.6;
      }
      return this.isConnected && this.visible && !document.hidden
        && (this.launchRoot?.sceneVisibility?.(this) ?? 1) >= 0.6;
    }

    canAutoplay() {
      return !this.motionQuery.matches && this.dataset.designMode !== 'true'
        && this.launchRoot?.dataset.designMode !== 'true';
    }

    canPlayVideo() {
      return this.isActive() && !this.userPaused && !this.autoplayBlocked
        && !this.failedSources.has(this.videoSource()) && (this.manualPlayback || this.canAutoplay());
    }

    syncControls() {
      const source = this.videoSource();
      const available = !!(this.video && source);
      const failed = this.failedSources.has(source);
      const playing = !!(this.playing && !this.video?.paused);
      const muted = !!(this.video?.muted || this.video?.volume === 0);
      this.toggleAttribute('data-film-playing', playing);
      this.toggleAttribute('data-film-muted', muted);
      this.toggleAttribute('data-film-error', failed);
      if (this.controls) this.controls.hidden = !available;
      const playLabel = failed ? this.labels.retry : playing ? this.labels.pause : this.labels.play;
      const soundLabel = muted ? this.labels.unmute : this.labels.mute;
      if (playLabel) this.setText(this.playLabel, playLabel);
      if (soundLabel) this.setText(this.soundLabel, soundLabel);
      if (this.playButton) {
        this.playButton.disabled = !available;
        this.playButton.setAttribute('aria-busy', String(!!this.playPending));
        if (playLabel) this.playButton.setAttribute('aria-label', playLabel);
      }
      if (this.soundButton) {
        this.soundButton.disabled = !available || failed;
        if (soundLabel) this.soundButton.setAttribute('aria-label', soundLabel);
      }
      this.setText(this.status, failed ? this.labels.error || '' : '');
    }

    setText(element, text) {
      if (!element) return;
      const reveal = this.launchRoot?.clockReveal;
      if (reveal?.setText) reveal.setText(element, text);
      else if (element.textContent !== text) element.textContent = text;
    }

    stopVideo({ hide = true } = {}) {
      this.videoGeneration = (this.videoGeneration || 0) + 1;
      this.playPending = false;
      this.playing = false;
      if (!this.video) return;
      this.video.pause();
      if (hide) {
        // Preserve the visitor's preference separately from this safety mute.
        this.video.muted = true;
        this.video.hidden = true;
      }
      this.syncControls();
    }

    failSource(source) {
      if (!source || source !== this.video.getAttribute('src')) return;
      this.failedSources.add(source);
      this.manualPlayback = false;
      this.stopVideo();
    }

    updateVideo({ forcePlay = false, reload = false } = {}) {
      if (!this.video) return;
      const source = this.videoSource();
      const active = this.isActive();
      if (active && !this.wasActive) {
        this.autoplayMuted = false;
        this.autoplayBlocked = false;
      }
      this.wasActive = active;
      if (!active) this.manualPlayback = false;
      if (!source || !this.canPlayVideo()) {
        this.stopVideo({ hide: !this.isActive() || !this.userPaused || source !== this.video.getAttribute('src') });
        return;
      }
      if (reload || this.video.getAttribute('src') !== source) {
        this.stopVideo();
        this.video.src = source;
        this.video.load();
      }
      this.video.muted = this.userMuted || this.autoplayMuted;
      if (forcePlay && this.playPending) {
        this.videoGeneration += 1;
        this.playPending = false;
      }
      if (this.playPending || (!forcePlay && !this.video.paused)) {
        this.syncControls();
        return;
      }
      this.playVideo();
    }

    playVideo(allowMutedFallback = true) {
      const source = this.video.getAttribute('src');
      const generation = this.videoGeneration;
      this.playPending = true;
      this.syncControls();
      const rejected = error => {
        if (generation !== this.videoGeneration) return;
        this.playPending = false;
        if (error?.name === 'NotAllowedError') {
          // Autoplay permission is not a broken file. Try the same video muted.
          if (!this.video.muted && allowMutedFallback && this.canPlayVideo()) {
            this.autoplayMuted = true;
            this.video.muted = true;
            this.playVideo(false);
          } else {
            this.autoplayBlocked = true;
            this.stopVideo();
          }
        } else if (error?.name === 'AbortError') {
          this.autoplayBlocked = true;
          this.stopVideo();
        } else this.failSource(source);
      };
      try {
        // Keep this call synchronous when invoked by either button's click.
        Promise.resolve(this.video.play()).then(() => {
          if (generation !== this.videoGeneration) return;
          this.playPending = false;
          if (!this.canPlayVideo() || source !== this.videoSource()) this.stopVideo();
          else this.syncControls();
        }).catch(rejected);
      } catch (error) {
        rejected(error);
      }
    }

    togglePlayback() {
      if (!this.video || !this.isActive()) return;
      if (this.playPending || !this.video.paused) {
        this.userPaused = true;
        this.manualPlayback = false;
        this.stopVideo({ hide: false });
        return;
      }
      this.userPaused = false;
      this.manualPlayback = true;
      this.autoplayBlocked = false;
      const reload = this.failedSources.delete(this.videoSource());
      this.updateVideo({ reload });
    }

    toggleSound() {
      if (!this.video || !this.isActive()) return;
      this.userMuted = !(this.video.muted || this.video.volume === 0);
      this.autoplayMuted = false;
      this.video.muted = this.userMuted;
      if (!this.userMuted) {
        if (this.video.volume === 0) this.video.volume = 1;
        this.userPaused = false;
        this.manualPlayback = true;
        this.autoplayBlocked = false;
        this.updateVideo({ forcePlay: true });
      } else this.syncControls();
    }

    refresh() {
      if (!this.isConnected) return;
      this.animation?.scrollTrigger?.refresh();
      this.updateVideo();
    }

    destroyMotion() {
      this.animation?.scrollTrigger?.kill(true);
      this.animation?.kill();
      this.animation = null;
      if (!this.media) return;
      if (window.gsap) window.gsap.set(this.media, { clearProps: 'transform' });
      else this.media.style.removeProperty('transform');
    }
  });
}
